import {createTheme, Theme} from "@mui/material/styles";
import type {} from "@mui/x-date-pickers/themeAugmentation";
import {CapasMatu, SemanticSet, previewTokens, tokens} from "./tokens";
import {makeComponentTokens} from "./componentTokens";
import {identity} from "./identity";

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    action: true;
    warning: true;
  }
}

export type Mode = "light" | "dark";

type Override = {[K in keyof SemanticSet]?: Partial<SemanticSet[K]>};

/**
 * Un borrador de tema: lo que el banco de diseño está probando ahora mismo.
 *
 * No se persiste ni se emite — existe solo mientras alguien está decidiendo. Lo
 * que se conserva es su exportación, que va al bloque `theme` de la
 * configuración del proyecto y de ahí alcanza tambien a los correos y a la
 * portada. El borrador solo alcanza a esta pantalla, y esa asimetria es a
 * proposito: sirve para explorar, no para quedarse.
 */
/**
 * El nombre del canal por el que las pestañas se cuentan qué tema están probando.
 *
 * Vive acá y no en cada punta porque las dos lo necesitan y tienen que coincidir:
 * el laboratorio emite y `PrefStore` escucha. Con un nombre declarado en cada
 * lado, la difusión llegaba solo a otro laboratorio y una pestaña con la
 * aplicación abierta no se enteraba de nada — y nada avisaba, porque dos canales
 * con nombres distintos funcionan los dos, cada uno solo.
 *
 * Es el mismo texto que la clave del borrador en `sessionStorage`, y eso también
 * es a propósito: es el mismo objeto viajando por dos vías.
 */
export const CANAL_DEL_TEMA = "themeDraft";

export interface ThemeDraft {
  light?: Override;
  dark?: Override;
  primitives?: Record<string, unknown>;
  components?: Record<string, unknown>;
  /**
   * Las capas del corpus del tema que este borrador resuelve.
   *
   * No la consume `buildTheme` —las capas no pintan nada—, sino la galeria:
   * "Paleta de color" y "Capas de contraste" muestran las rampas del tema, y
   * esas rampas NO estan en el conjunto semantico ni en los primitivos
   * (`primitives.scales` viene vacio en todos los presets). Sin este campo, la
   * galeria solo podia mostrar las capas de la CONFIGURACION mientras la
   * pantalla ya estaba pintada con otro tema.
   */
  capas?: CapasMatu | null;
}

/**
 * Fusiona en profundidad, pero solo donde hay objetos. Los arrays —las sombras—
 * se reemplazan enteros: fusionar una escala de elevacion por indice produciria
 * una mezcla que nadie eligio.
 *
 * Sin esto, tocar un solo paso de la escala de radios borraria los otros seis.
 */
function deepMerge<T>(base: T, override: unknown): T {
  if (override === undefined || override === null) return base;
  if (Array.isArray(override) || typeof override !== "object") return override as T;

  const out: Record<string, unknown> = {...(base as unknown as Record<string, unknown>)};
  for (const [key, value] of Object.entries(override as Record<string, unknown>)) {
    out[key] =
      value && typeof value === "object" && !Array.isArray(value)
        ? deepMerge(out[key], value)
        : value;
  }
  return out as unknown as T;
}

/**
 * Fusiona capas sobre el conjunto del tema, en orden de precedencia creciente.
 * Es superficial por superficie: cambiar un fondo no obliga a redeclarar el
 * color del texto que va encima.
 */
function merge(base: SemanticSet, ...layers: (Override | undefined)[]): SemanticSet {
  let out = base as unknown as Record<string, unknown>;

  for (const layer of layers) {
    if (!layer) continue;
    out = {...out};
    for (const [key, value] of Object.entries(layer)) {
      const current = out[key];
      out[key] =
        value && typeof value === "object" && !Array.isArray(value) && current
          ? {...(current as object), ...(value as object)}
          : value;
    }
  }

  return out as unknown as SemanticSet;
}

/**
 * El objeto que se le pasa a `createTheme`, separado de la llamada.
 *
 * Están separados para poder comprobar la equivalencia sin instanciar MUI: el
 * criterio de corrección de esta reorganización es que este objeto sea igual al
 * que el tema producía cuando los valores estaban escritos a mano, y eso se
 * verifica comparando objetos, no capturas.
 */
/**
 * Los tokens EFECTIVOS: lo que el tema realmente vale una vez aplicadas la
 * identidad del proyecto y el borrador que se este probando.
 *
 * Existe como funcion propia porque tiene DOS consumidores —el constructor del
 * tema y la galeria, que muestra esos mismos valores como fundamentos— y si
 * cada uno fusionara por su cuenta, la galeria terminaria mostrando numeros
 * distintos de los que la pantalla de al lado esta pintando. Eso es justo lo
 * que la galeria tiene prohibido: tener su propia version de algo.
 *
 * Orden de precedencia: los tokens generados, despues la identidad del
 * proyecto, y arriba de todo el borrador. El borrador gana porque es lo que
 * alguien esta mirando en este momento.
 */
export function tokensEfectivos(draft?: ThemeDraft | null, preview = false) {
  // Cual de los dos temas: el oficial, o el que solo ve la audiencia en
  // pruebas. La decision de QUIEN entra la toma el store; aca solo se aplica.
  const set = preview && previewTokens ? previewTokens : tokens;
  return {
    preset: set.preset,
    primitives: deepMerge(set.primitives, draft?.primitives),
    light: merge(set.light, identity.light, draft?.light),
    dark: merge(set.dark, identity.dark, draft?.dark),
  };
}

export function themeOptions(mode: Mode, draft?: ThemeDraft | null, preview = false) {
  const efectivo = tokensEfectivos(draft, preview);
  const c = efectivo[mode];
  const p = efectivo.primitives;
  // El nivel 3 se recalcula con los primitivos EFECTIVOS: si no, mover la
  // escala de radios no movería ningún componente.
  const k = deepMerge(makeComponentTokens(p), draft?.components);

  /**
   * Una línea del tema: su grosor sale del primitivo, no del número 1.
   *
   * Doce lugares escribían "1px solid" a mano, y el primitivo `stroke.outline`
   * —que el sistema de diseño declara desde siempre— no lo leía ninguno. El
   * síntoma era un ajuste inerte: se movía el grosor de línea, el token cambiaba,
   * y ni un borde de la pantalla se movía.
   *
   * El respaldo es 1 porque `stroke` es opcional en los primitivos: un preset que
   * no lo declare dibuja lo que dibujaba antes.
   */
  const trazo = (color: string) => (p.stroke?.outline ?? 1) + "px solid " + color;

  /**
   * La rampa tipográfica, traducida a lo que MUI entiende.
   *
   * El sistema de diseño distingue tres familias —párrafo, títulos y botones— y
   * MUI conoce UNA (`fontFamily`) más la que cada variante declare. Las otras dos
   * viajaban como `titleFontFamily` y `buttonFontFamily` dentro del objeto de
   * tipografía, y MUI las IGNORABA: eran claves que no significan nada para él.
   *
   * El síntoma era exactamente eso — se elegía una letra para los botones o para
   * los títulos, el token cambiaba, y la pantalla no. Acá se reparten donde MUI
   * las lee: la de títulos en las seis variantes de encabezado, la de botones en
   * la variante `button`.
   */
  const tipografiaDeMui = (t: typeof p.typography) => {
    const {titleFontFamily, buttonFontFamily, ...resto} = t;
    const titulos: Record<string, unknown> = {};
    for (const variante of ["h1", "h2", "h3", "h4", "h5", "h6"] as const) {
      const suya = (resto as Record<string, object | undefined>)[variante];
      if (suya) titulos[variante] = {...suya, fontFamily: titleFontFamily};
    }
    return {
      ...resto,
      ...titulos,
      button: {...resto.button, fontFamily: buttonFontFamily},
    };
  };

  /**
   * El anillo de foco, en UN solo lugar.
   *
   * Lo aplican tres familias de componentes y antes estaba escrito una vez, en
   * `MuiButtonBase`. Extenderlo copiando el bloque habría dejado tres
   * declaraciones del mismo anillo: la primera vez que alguien cambie el grosor
   * en una y no en las otras, el foco se ve distinto según qué se enfoque, y nada
   * lo denuncia.
   *
   * `:focus-visible` y no `:focus`: quien carga datos todo el día opera con el
   * teclado por velocidad, y el anillo no tiene que aparecer al hacer clic.
   */
  const anilloDeFoco = {
    "&:focus-visible": {
      outline: `${p.focus.width}px ${p.focus.style} ${c.focusRing}`,
      outlineOffset: p.focus.offset,
    },
  };

  return {
    shadows: p.shadows as Theme["shadows"],

    // Los defaults de MUI (modal 1300, snackbar 1400, tooltip 1500) son
    // demasiado bajos: esta app usa una "banda alta" para modales/menús
    // anidados —
    //   10100 ModalView · 10200 diálogo de edición anidada (ItemsCRUDCompact)
    //   / Autocomplete dentro de mini-form · 10250 MuiMenu (Select/acciones)
    //   · 10300 mini-form anidado nivel 2.
    // Las capas que SIEMPRE deben quedar por ENCIMA de todo modal (porque
    // pueden dispararse DESDE dentro de uno) suben sobre esa banda:
    //   snackbar 10350 (notificaciones) · tooltip 10400 (ayuda contextual,
    //   p.ej. FieldHelpIcon de las descripciones de campo).
    // Antes el tooltip quedaba en 1500 → detrás de los modales (descripción
    // de campo invisible dentro de un mini-form). Cualquier tooltip nuevo
    // hereda 10400 sin necesidad de override por componente.
    zIndex: {
      snackbar: p.layers.snackbar,
      tooltip: p.layers.tooltip,
    },

    shape: {
      borderRadius: p.radius.s,
    },

    typography: tipografiaDeMui(p.typography),

    // 6.7. Hasta ahora el sistema no declaraba ninguna transicion y MUI aplicaba
    // las suyas: el movimiento existia, pero nadie lo habia decidido. Los
    // nombres de MUI se mapean a los del sistema, no al reves.
    transitions: {
      duration: {
        shortest: p.motion.duration.instant || 1,
        shorter: p.motion.duration.fast,
        short: p.motion.duration.fast,
        standard: p.motion.duration.base,
        complex: p.motion.duration.slow,
        enteringScreen: p.motion.duration.base,
        leavingScreen: p.motion.duration.fast,
      },
      easing: {
        easeInOut: p.motion.easing.standard,
        easeOut: p.motion.easing.entering,
        easeIn: p.motion.easing.exiting,
        sharp: p.motion.easing.emphasized,
      },
    },

    palette: {
      primary: {
        main: c.primary.bg,
        ...(c.primary.subtle ? {light: c.primary.subtle} : {}),
        ...(c.primary.hover ? {dark: c.primary.hover} : {}),
        contrastText: c.primary.on,
      },
      secondary: {
        main: c.secondary.bg,
        ...(c.secondary.subtle ? {light: c.secondary.subtle} : {}),
        ...(c.secondary.hover ? {dark: c.secondary.hover} : {}),
        contrastText: c.secondary.on,
      },
      background: {
        default: c.page.bg,
        paper: c.raised.bg,
      },
      text: {
        primary: c.text.primary,
        secondary: c.text.secondary,
        ...(c.text.disabled ? {disabled: c.text.disabled} : {}),
      },
      divider: c.divider,
      error: {
        main: c.error.bg,
        contrastText: c.error.on,
        ...(c.error.subtle ? {light: c.error.subtle} : {}),
        ...(c.error.hover ? {dark: c.error.hover} : {}),
      },
      warning: {
        main: c.warning.bg,
        contrastText: c.warning.on,
        ...(c.warning.subtle ? {light: c.warning.subtle} : {}),
        ...(c.warning.hover ? {dark: c.warning.hover} : {}),
      },
      success: {
        main: c.success.bg,
        contrastText: c.success.on,
        ...(c.success.subtle ? {light: c.success.subtle} : {}),
        ...(c.success.hover ? {dark: c.success.hover} : {}),
      },
      // `info` solo se declara si el tema lo define. Si falta, MUI aplica el
      // suyo: declararlo con un solo `main` haría que derive `light` y `dark`
      // distintos de sus defaults, y cambiaría la pantalla sin que nadie lo
      // haya pedido.
      ...(c.info
        ? {
            info: {
              main: c.info.bg,
              contrastText: c.info.on,
              ...(c.info.subtle ? {light: c.info.subtle} : {}),
              ...(c.info.hover ? {dark: c.info.hover} : {}),
            },
          }
        : {}),
      // 6.1: el puntero encima y lo seleccionado son decisiones del tema, no
      // grises que MUI inventa. Sin esto, cambiar el hover de una superficie no
      // movia ninguna fila de ninguna grilla.
      action: {
        hover: c.raised.hover,
        selected: c.sunken.hover ?? c.raised.hover,
      },
      mode,
    },

    spacing: p.spacingUnit,

    components: {
      /**
       * El grosor del trazo de los íconos.
       *
       * Vive ACÁ y no horneado en el registro de íconos, y esa es la diferencia:
       * el registro lo escribía como una constante en generación
       * (`const TRAZO = ...`), así que el laboratorio podía moverlo y la pantalla
       * no cambiaba — el valor estaba congelado en el archivo. Como override del
       * tema se re-pinta con cada cambio, que es lo que un laboratorio necesita.
       *
       * No hace falta condicionarlo por familia: en una familia rellena no hay
       * trazo que engrosar, así que el valor no se ve.
       */
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            strokeWidth: p.icons.strokeWidth,
          },
        },
      },

      MuiButtonBase: {
        defaultProps: {
          disableRipple: true,
        },
        styleOverrides: {
          // 6.8. El foco visible no es una concesion: quien carga datos todo el
          // dia opera con el teclado por velocidad. Ver `anilloDeFoco`.
          root: anilloDeFoco,
        },
      },

      /**
       * Un ENLACE también recibe el anillo.
       *
       * No lo recibía, y era una brecha de accesibilidad real: `MuiLink` no sale
       * de `ButtonBase`, así que un enlace enfocado con el teclado quedaba con el
       * anillo que el navegador quiera darle —o con ninguno, si el reset de la
       * aplicación lo apagó—. 1.4.11 y 2.4.7 piden un indicador visible en TODO
       * lo que reciba foco, y el anillo del sistema es el que corresponde: un
       * campo tiene su propio borde, un enlace no tiene nada.
       */
      MuiLink: {
        styleOverrides: {
          root: anilloDeFoco,
        },
      },

      /**
       * Y un CHIP que se puede tocar o borrar. Tampoco sale de `ButtonBase`: su
       * raíz es un `div` con su propia gestión de foco, así que quedaba en el
       * mismo hueco que el enlace.
       */
      MuiChip: {
        styleOverrides: {
          root: {
            ...anilloDeFoco,
            borderRadius: k.chip.radius,
            fontWeight: 500,
            fontSize: k.chip.fontSize,
          },
        },
      },

      MuiButton: {
        variants: [
          {
            props: {variant: "action" as const},
            style: {
              textTransform: "none",
              color: c.primary.bg,
              paddingRight: 0,
              paddingLeft: k.button.variantPaddingLeft,
            },
          },
          {
            props: {variant: "warning" as const},
            style: {
              textTransform: "none",
              color: c.error.bg,
              paddingRight: 0,
              paddingLeft: k.button.variantPaddingLeft,
            },
          },
        ],
        styleOverrides: {
          root: {
            borderRadius: k.button.radius,
            padding: `${k.button.paddingY}px ${k.button.paddingX}px`,
            fontSize: k.button.fontSize,
            boxShadow: "none",
            "&:hover": {
              boxShadow: "none",
            },
          },
          contained: {
            "&:hover": {
              boxShadow: "none",
            },
          },
        },
      },

      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            border: trazo(c.raised.border),
          },
        },
      },

      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            border: trazo(c.raised.border),
            borderRadius: k.card.radius,
          },
        },
      },

      // El fondo y el texto del encabezado siguen saliendo de `sunken`, y no de
      // la familia: `families.grid.headBg` vale hoy `sunken.bg` en los seis
      // presets, asi que lo que se lee encima esta medido por los pares
      // `sunken.on/bg` y `sunken.onMuted/bg`. Darle texto propio a la grilla
      // seria vocabulario para una decision que ningun preset tomo.
      //
      // `borderBottom` es la SEGUNDA declaracion de la linea del encabezado —la
      // primera es la de la celda, que gana por resolucion de bordes colapsados
      // y por orden de pintado— y por eso NO pasa a la familia: un token para
      // una linea que no se ve es vocabulario que nadie puede mantener. Los
      // cinco presets que la derivan ya la hacen coincidir con la de la celda;
      // solo `legacy` difiere (#F0F0F0 contra #E5E5E5), y ahi es donde se ve
      // que es una transcripcion duplicada y no una decision propia.
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: c.sunken.bg,
            color: c.sunken.on,
            "& .MuiTableSortLabel-root": {color: c.sunken.onMuted},
            borderBottom: trazo(c.sunken.borderStrong ?? c.sunken.border),
          },
        },
      },

      MuiTableCell: {
        styleOverrides: {
          root: {
            // `"none"` explícito y no la clave ausente: MUI trae su propia
            // línea, así que no declarar nada devuelve la de la librería en vez
            // de sacarla. El encabezado NO participa —su borde separa dos zonas,
            // no dos filas— y se declara abajo, en las tres alternativas.
            borderBottom:
              k.table.rows === "linea" ? trazo(c.families.grid.border) : "none",
            padding: `${k.table.cellPaddingY}px ${k.table.cellPaddingX}px`,
            fontSize: k.table.fontSize,
          },
          head: {
            fontWeight: 500,
            color: c.text.secondary,
            fontSize: k.table.headFontSize,
            backgroundColor: c.families.grid.headBg,
            borderBottom: trazo(c.families.grid.headBorder),
          },
        },
      },

      MuiTableRow: {
        styleOverrides: {
          root: {
            // 6.7: "las operaciones frecuentes suelen ganar sin animacion".
            // Recorrer una grilla es la mas frecuente de todas.
            ...(p.motion.neverAnimate.includes("tableRowHover")
              ? {transition: "none"}
              : {}),
            // El rayado va ANTES del hover a propósito: las dos reglas tienen la
            // misma especificidad (una pseudoclase cada una), así que la que
            // gana es la última, y la que tiene que ganar es "estás acá".
            // `nth-of-type` cuenta entre hermanas, o sea dentro del cuerpo: la
            // fila del encabezado es la única de su grupo y nunca se raya.
            ...(k.table.rows === "rayado"
              ? {"&:nth-of-type(even)": {backgroundColor: c.families.grid.rowAlt}}
              : {}),
            "&:hover": {
              backgroundColor: c.families.grid.rowHover,
            },
          },
        },
      },

      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: k.input.radius,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: c.families.field.border,
            },
            // El `??` desaparecio con el token: el borde bajo el puntero es una
            // decision del campo, y quien la resuelve —haya o no un borde
            // enfatizado en la tarjeta— es la familia, una sola vez.
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: c.families.field.borderHover,
            },
            // El tercer estado. Hasta aca lo pintaba MUI con `primary.main`, que
            // es lo que `families.field.borderFocus` vale en todos los presets:
            // el color no cambia, cambia QUIEN lo decide. Sin esto el gate mide
            // un token que la pantalla no consume, que es el mismo agujero que
            // el par que nadie mide, dado vuelta.
            //
            // El `:not(.Mui-error)` no es defensivo de mas: los overrides del
            // tema entran DESPUES de los estilos base de MUI, asi que sin el
            // un campo con error y foco perderia el rojo.
            "&.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline": {
              borderColor: c.families.field.borderFocus,
            },
          },
        },
      },

      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: c.inverse.bg,
            color: c.inverse.on,
            border: trazo(c.inverse.border),
            fontSize: k.tooltip.fontSize,
            borderRadius: k.tooltip.radius,
            padding: `${k.tooltip.paddingY}px ${k.tooltip.paddingX}px`,
          },
          arrow: {color: c.inverse.bg},
        },
      },

      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: k.dialog.radius,
            boxShadow: k.dialog.shadow,
            border: "none",
          },
        },
      },

      // El chip ya tenía su bloque de forma; el anillo se declara arriba junto a
      // los otros dos. Se fusionan acá y no en dos claves `MuiChip` —la segunda
      // pisaría a la primera en silencio.


      MuiAppBar: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            borderBottom: trazo(c.appBar.border),
            backgroundColor: c.appBar.bg,
            color: c.appBar.on,
            "& .MuiIconButton-root": {color: c.appBar.onMuted},
            "& .MuiIconButton-root:hover": {backgroundColor: c.appBar.hover},
            "& .MuiDivider-root": {borderColor: c.appBar.borderStrong ?? c.appBar.border},
          },
        },
      },

      // Los avisos son donde las superficies de estado se ven enteras: fondo,
      // texto, texto secundario y borde. Sin esto, cuatro de los siete campos de
      // error/advertencia/exito/informacion no llegaban a ningun lado.
      //
      // Un estado trae DOS registros y son pares completos, no colores sueltos:
      // el relleno pleno (`bg` con su `on`) y el tinte (`subtle` con su
      // `onSubtle`). El Alert standard pinta el tinte, asi que se lee con el ink
      // del tinte — el `on` del relleno encima de `subtle` es texto claro sobre
      // fondo claro. Cuando el tema no declara el tinte (sin `subtle`), el
      // fondo cae al relleno y el texto a su `on`: el par sigue siendo par.
      MuiAlert: {
        styleOverrides: {
          standardError: {
            backgroundColor: c.error.subtle ?? c.error.bg,
            color: (c.error.subtle && c.error.onSubtle) || c.error.on,
            border: trazo(c.error.border),
          },
          standardWarning: {
            backgroundColor: c.warning.subtle ?? c.warning.bg,
            color: (c.warning.subtle && c.warning.onSubtle) || c.warning.on,
            border: trazo(c.warning.border),
          },
          standardSuccess: {
            backgroundColor: c.success.subtle ?? c.success.bg,
            color: (c.success.subtle && c.success.onSubtle) || c.success.on,
            border: trazo(c.success.border),
          },
          ...(c.info
            ? {
                standardInfo: {
                  backgroundColor: c.info.subtle ?? c.info.bg,
                  color: (c.info.subtle && c.info.onSubtle) || c.info.on,
                  border: trazo(c.info.border),
                },
              }
            : {}),
          // El outlined no pinta fondo: su texto se apoya en la superficie de
          // abajo. El ink del tinte tambien contrasta ahi (el tinte es mas
          // intenso que la pagina), y a diferencia del que MUI deriva conserva
          // el hue del tema. Solo cuando el tema lo declara: sin `onSubtle`,
          // el derivado de MUI sigue —el `on` de un relleno pleno seria texto
          // claro sobre papel claro.
          outlinedError: {
            borderColor: c.error.borderStrong ?? c.error.border,
            ...(c.error.onSubtle ? {color: c.error.onSubtle} : {}),
          },
          outlinedWarning: {
            borderColor: c.warning.borderStrong ?? c.warning.border,
            ...(c.warning.onSubtle ? {color: c.warning.onSubtle} : {}),
          },
          outlinedSuccess: {
            borderColor: c.success.borderStrong ?? c.success.border,
            ...(c.success.onSubtle ? {color: c.success.onSubtle} : {}),
          },
          ...(c.info
            ? {
                outlinedInfo: {
                  borderColor: c.info.borderStrong ?? c.info.border,
                  ...(c.info.onSubtle ? {color: c.info.onSubtle} : {}),
                },
              }
            : {}),
        },
      },

      // El texto secundario NO es uno solo: cada superficie declara el suyo,
      // porque el gris que se lee sobre una tarjeta no es el que se lee sobre
      // una superficie hundida. MUI tiene un unico `text.secondary`, asi que
      // cada superficie tiene que declararlo donde vive.
      MuiCardContent: {
        styleOverrides: {
          root: {
            color: c.raised.on,
            "& .MuiTypography-body2": {color: c.raised.onMuted},
          },
        },
      },

      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: c.sidebar.bg,
            color: c.sidebar.on,
            borderRight: trazo(c.sidebar.border),
          },
        },
      },

      // El item del menu: reposo, puntero encima y activo salen del tema. Antes
      // los tres eran grises de MUI, asi que el menu no acompaniaba al resto.
      MuiListItemButton: {
        styleOverrides: {
          root: {
            "&:hover": {backgroundColor: c.sidebar.hover},
            "&.Mui-selected": {
              backgroundColor: c.sidebar.subtle ?? c.sidebar.hover,
              color: c.sidebar.onSelected ?? c.sidebar.on,
              "&:hover": {backgroundColor: c.sidebar.hover},
            },
          },
        },
      },

      MuiListItemIcon: {
        styleOverrides: {
          root: {
            color: c.sidebar.onMuted,
            minWidth: p.spacing.xl,
          },
        },
      },

      // Los menús (Select dropdown, menú de acciones de fila/columna de
      // MaterialReactTable) usan z-index modal por default (1300). Cuando
      // se abren dentro de un Dialog de edición anidada — que esta app
      // fija en 10200 (ItemsCRUDCompact) para ganarle a ModalView (10100)
      // — quedan detrás del diálogo y parecen no responder. Los subimos a
      // la banda alta: por encima del diálogo (10200) y por debajo del
      // tooltip de InputReferenceSet (10300). MaterialReactTable no expone
      // props para inyectar z-index a su menú de acciones, así que el
      // override de theme es el único punto de control.
      MuiMenu: {
        styleOverrides: {
          root: {
            zIndex: p.layers.menu,
          },
        },
      },
    },
  };
}

export function buildTheme(mode: Mode, draft?: ThemeDraft | null, preview = false): Theme {
  // @ts-ignore
  return createTheme(themeOptions(mode, draft, preview));
}

/**
 * Si este usuario entra en la audiencia del tema en pruebas.
 *
 * Falla CERRADO: sin tema en pruebas, sin usuario, o sin audiencia que lo
 * incluya, se devuelve el tema oficial. Un error aca no puede terminar en que
 * una editorial vea por accidente un diseño a medio decidir.
 */
export function isPreviewAudience(user: {
  role?: {id?: string | null} | null;
  company?: {name?: string | null} | null;
} | null | undefined): boolean {
  if (!previewTokens || !user) return false;
  const {audience, systemOwnerTenant} = previewTokens;

  const rol = user.role?.id;
  if (rol && audience.roles?.includes(rol)) return true;

  const tenant = user.company?.name;
  if (!tenant) return false;
  if (audience.systemOwner && tenant === systemOwnerTenant) return true;
  return audience.tenants?.includes(tenant) ?? false;
}
